import { useRouter } from 'next/router'
import styled, { useTheme } from 'styled-components'
import { BackActionMap } from '@/components/common/back-action/types'
import { useBackAction } from '@/components/common/back-action/use-back-action'
import { Typo, TypoVariant } from '@/components/common/typography'
import { ScrollContainer } from '@/components/domains/etc'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import SkeletonProduct from '@/components/domains/products/skeleton/SkeletonProduct'
import { ProductLocation } from '@/constants/product-location.const'
import { ProductThumbnailSizeEnum } from '@/constants/product-thumbnail-size.enum'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { useCategoryTracker } from '@/containers/event-tracker/category.event-tracker'
import { useSearchEventTracker } from '@/containers/event-tracker/search.event-tracker'
import { useJustViewedProductContainerHook } from '@/containers/hooks/just-viewed-product.container.hook'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import { encodeId } from '@/utils/utilApi'
import type { AdPetType } from '@/graphql/generated/schemas'
import type { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'

type HandleClickProductCardTracker = ({
  item,
  index,
  properties,
}: {
  item: ProductTypeDefinedByFrontend
  index: number
  properties?: Record<string, any>
}) => void

interface JustViewedProductListProps {
  name: BackActionMap['name']
  justViewedProduct: ProductTypeDefinedByFrontend
  onClickProductCardTracker?: HandleClickProductCardTracker
  trackerData: {
    location: 'search' | 'category'
    addLocation: 'search_result' | 'category'
    categoryName?: string
    categoryTypeName?: string
  }
}

export const JustViewedProductList = ({
  name,
  justViewedProduct,
  onClickProductCardTracker,
  trackerData,
}: JustViewedProductListProps) => {
  const { color } = useTheme()
  const { query } = useRouter()
  const backAction = useBackAction(name)

  const {
    [QueryStringKeyEnum.SearchLocation]: searchLocation,
    [QueryStringKeyEnum.SearchQuery]: searchKeywordQueryParam,
    [QueryStringKeyEnum.SearchPetType]: petTypeQueryParam,
  } = query

  const { trackViewSearchResultSimilarProductSection } = useSearchEventTracker()
  const { trackViewCategorySimilarProductSection } = useCategoryTracker()

  const { isLoading, recommendedSimilarProducts } = useJustViewedProductContainerHook({
    productId: encodeId('ProductType', justViewedProduct.id),
    onCompleted: () => {
      if (name === 'recommend-list-for-search-result') {
        const addLocation = backAction.front()?.condition.includes('bought-product')
          ? `${trackerData.addLocation}_frequent`
          : trackerData.addLocation

        trackViewSearchResultSimilarProductSection({
          addLocation,
          sectionName: '방금 본 상품과 비슷한 상품',
          searchKeyword: searchKeywordQueryParam as string,
          searchLocation: searchLocation as string,
          originSupplyType: justViewedProduct.supplyType,
          originBrandId: justViewedProduct.brand.id,
          originBrandName: justViewedProduct.brand.name,
          originProductId: justViewedProduct.id,
          originProductName: justViewedProduct.name,
          petTypeSearch: petTypeQueryParam as AdPetType,
        })
        return
      }

      trackViewCategorySimilarProductSection({
        originProductId: justViewedProduct.id,
        originProductName: justViewedProduct.name,
        originSupplyType: justViewedProduct.supplyType,
        originBrandId: justViewedProduct.brand?.id,
        originBrandName: justViewedProduct.brand.name,
        categoryName: trackerData.categoryName,
        categoryTypeName: trackerData.categoryTypeName,
      })
    },
  })

  const handleClickSimilarProduct = (product: ProductTypeDefinedByFrontend, index: number) => {
    if (name === 'recommend-list-for-search-result') {
      onClickProductCardTracker?.({
        item: product,
        index,
        properties: {
          listType: 'similarproduct',
          addLocation: `${trackerData.addLocation}_frequent`,
          sectionName: '방금 본 상품과 비슷한 상품',
          supplyType: product.supplyType,
          listIndex: index,
          brandId: product.brand?.id,
          brandName: product.brand.name,
          originProductId: justViewedProduct.id,
          originProductName: justViewedProduct.name,
          petTypeSearch: petTypeQueryParam as AdPetType,
        },
      })
      return
    }

    onClickProductCardTracker?.({
      item: product,
      index,
      properties: {
        listType: 'similarproduct',
        listIndex: index,
        sectionName: '방금 본 상품과 비슷한 상품',
        originProductId: justViewedProduct.id,
        originProductName: justViewedProduct.name,
        location: trackerData.location,
        addLocation: trackerData.addLocation,
        categoryName: trackerData.categoryName,
        categoryTypeName: trackerData.categoryTypeName,
        supplyType: product.supplyType,
        brandId: product.brand?.id,
        brandName: product.brand.name,
      },
    })
  }

  if (isLoading) {
    return (
      <StyledSection style={{ paddingLeft: '1.6rem' }}>
        <StyledSkeletonHeader style={{ padding: '0' }}>
          <StyledSkeletonAvatar />
          <StyledSkeletonText />
        </StyledSkeletonHeader>
        <StyledSkeletonProductWrapper style={{ padding: '0' }}>
          <SkeletonProduct size={ProductThumbnailSizeEnum.Card136} />
        </StyledSkeletonProductWrapper>
      </StyledSection>
    )
  }

  return (
    <>
      <StyledSection>
        <StyledHeader>
          <StyledAvatar>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={justViewedProduct.thumbnail} alt="just-viewed-product-list-thumbnail" />
          </StyledAvatar>
          <div>
            <Typo variant={TypoVariant.Heading6Bold} color={color.blue500}>
              방금 본{' '}
              <Typo variant={TypoVariant.Heading6Bold} color={color.gray900}>
                상품과 비슷한 상품
              </Typo>
            </Typo>
          </div>
        </StyledHeader>
        <ScrollContainer hideRightButton={recommendedSimilarProducts.length < 3} buttonPositionY={30}>
          <StyledBlockListDiv className="product-detail-similar-product-list__list">
            {recommendedSimilarProducts.map((product, index) => {
              const productData = transformProductTypeDefinedByFrontend(product)
              return (
                <div className="product-detail-similar-product-list__item" key={productData.id}>
                  <NewProductCard
                    hasCartButton
                    product={productData}
                    size={PRODUCT_CARD_SIZES.lg}
                    onClickProductCard={() => handleClickSimilarProduct(productData, index)}
                    trackerData={{
                      location: trackerData.location,
                      addLocation:
                        name === 'recommend-list-for-search-result'
                          ? ProductLocation.SearchResult
                          : ProductLocation.Category,
                      sectionName: '방금 본 상품과 비슷한 상품',
                      listType: 'similarproduct',
                      searchKeyword: searchKeywordQueryParam as string,
                      searchLocation: searchLocation as string,
                      petTypeSearch: petTypeQueryParam as AdPetType,
                      categoryName: trackerData.categoryName,
                      categoryTypeName: trackerData.categoryTypeName,
                      originProductId: justViewedProduct.id,
                      originProductName: justViewedProduct.name,
                      brandId: productData.brand?.id,
                      brandName: productData.brand.name,
                      supplyType: productData.supplyType,
                    }}
                  />
                </div>
              )
            })}
          </StyledBlockListDiv>
        </ScrollContainer>
      </StyledSection>
    </>
  )
}

const StyledSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  height: 4rem;
  padding: 0 1.6rem;
`

const StyledAvatar = styled.div`
  width: 4rem;
  height: 4rem;
  flex-shrink: 0;
  border-radius: 4rem;
  border: 1.5px solid ${({ theme }) => theme.color.blue500};
  overflow: hidden;

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const StyledBlockListDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(3, 1fr);
  grid-column-gap: 0.8rem;
  & > *:first-child {
    padding-left: 1.6rem;
  }
  & > *:last-child {
    padding-right: 1.6rem;
  }
`

const StyledSkeletonProductWrapper = styled.div`
  display: flex;
  gap: 0.8rem;
  overflow: hidden;
  padding-bottom: 2.4rem;
  padding: 0 1.6rem;
`

const StyledSkeletonHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  height: 4rem;
  padding: 0 1.6rem;
`

const StyledSkeletonAvatar = styled.div`
  width: 4rem;
  height: 4rem;
  flex-shrink: 0;
  border-radius: 4rem;
  border: 1.5px solid ${({ theme }) => theme.color.gray50};
  background-color: ${({ theme }) => theme.color.gray50};
`

const StyledSkeletonText = styled.div`
  width: 13.6rem;
  height: 2.5rem;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.color.gray50};
  border-radius: 0.4rem;
`
